import {MutableRefObject, useEffect, useLayoutEffect, useRef, useState} from "react";

type Tuple2D = [x: number, y: number];

type Particle = {
  pos: Tuple2D;
  dir: Tuple2D;
  speed: number;
  age: number;
  maxAge: number;
  isDead: boolean;
  color: string;
};

const normalizeInPlace = (p: Tuple2D): Tuple2D => {
  const len = Math.sqrt(p[0] * p[0] + p[1] * p[1]);
  if (len === 0) return p;
  p[0] /= len;
  p[1] /= len;
  return p;
};

const jitterInPlace = (p: Tuple2D, strength: number = 10) => {
  p[0] += (Math.random() - 0.5) * strength;
  p[1] += (Math.random() - 0.5) * strength;
  return p;
};

const setupParticles = (opts: {
  bounds: DOMRect;
  size: number;
  pixelsPerPoint: number;
  addParticle: (pos: Tuple2D) => Particle;
}): Particle[] => {
  const {addParticle, bounds, pixelsPerPoint, size} = opts;
  const h = bounds.height + size * 2;
  const w = bounds.width + size * 2;

  const particles: Particle[] = [];
  const horPointCount = Math.ceil(bounds.width / pixelsPerPoint);
  const horPixelsPerPoint = bounds.width / horPointCount;
  let startX = horPixelsPerPoint / 2;
  for (let i = 0; i < horPointCount; i += 1) {
    let x = size + startX + i * horPixelsPerPoint;
    particles.push(addParticle([x, size]), addParticle([x, h - size]));
  }

  const verPointCount = Math.ceil(bounds.height / pixelsPerPoint);
  const verPixelsPerPoint = bounds.height / verPointCount;
  let startY = verPixelsPerPoint / 2;
  for (let i = 0; i < verPointCount; i += 1) {
    let y = size + startY + i * verPixelsPerPoint;
    particles.push(addParticle([size, y]), addParticle([w - size, y]));
  }

  return particles;
};

const setupOuterParticles = (opts: {bounds: DOMRect; size: number}): Particle[] => {
  const {bounds, size} = opts;
  const h = bounds.height + size * 2;
  const w = bounds.width + size * 2;
  const center = [w / 2, h / 2];

  const addParticle = (pos: [x: number, y: number]): Particle => {
    const start = jitterInPlace(pos);
    const nearCenter = jitterInPlace([center[0], center[1]]);
    const dir = normalizeInPlace([start[0] - nearCenter[0], start[1] - nearCenter[1]]);
    return {
      pos: start,
      dir,
      speed: 5 + Math.random() * 1.5,
      age: -450 + Math.random() * -50,
      maxAge: 450 + Math.random() * 100,
      isDead: false,
      color: `hsl(${Math.round(40 + Math.random() * 10)},${Math.round(
        80 + Math.random() * 10
      )}%,${Math.round(58 + Math.random() * 10)}%)`,
    };
  };
  return setupParticles({addParticle, bounds, pixelsPerPoint: 10, size});
};

const starCols = ["#F1C950", "#FBE08E", "#F1AB50"];
const loadAllImgs = () =>
  Promise.all(
    starCols.map(
      (s) =>
        new Promise<void>((res, rej) => {
          ensureStarImg(s, [res, rej]);
        })
    )
  );

const setupStarParticles = (opts: {bounds: DOMRect; size: number}): Particle[] => {
  const {bounds, size} = opts;
  const h = bounds.height + size * 2;
  const w = bounds.width + size * 2;
  const center = [w / 2, h / 2];

  // const pointCount = Math.ceil((h + w) * 2 / 25);
  const addParticle = (pos: [x: number, y: number]): Particle => {
    const start = jitterInPlace(pos);
    const nearCenter = jitterInPlace([center[0], center[1]]);
    const dir = normalizeInPlace([start[0] - nearCenter[0], start[1] - nearCenter[1]]);
    return {
      pos: start,
      dir,
      speed: 1 + Math.random() * 2,
      age: -500 + Math.random() * -150,
      maxAge: 750 + Math.random() * 250,
      isDead: false,
      color: starCols[Math.floor(Math.random() * starCols.length)],
    };
  };
  return setupParticles({addParticle, bounds, pixelsPerPoint: 50, size});
};

const performAnim = (opts: {
  ctx: CanvasRenderingContext2D;
  bounds: DOMRect;
  size: number;
  onDone: () => void;
  node: HTMLElement;
}) => {
  const {ctx, onDone, node} = opts;
  const outerParticles = setupOuterParticles(opts);
  let stars: Particle[] = setupStarParticles(opts);

  const draw = () => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    for (const p of outerParticles) {
      if (p.isDead) continue;
      if (p.age < 0) continue;
      const relAge = p.age / p.maxAge;
      ctx.fillStyle = p.color;
      ctx.globalAlpha = Math.max(0, 1 - relAge);
      ctx.beginPath();
      const size = (1 / p.speed) * 15 * (relAge * 0.25 + 0.75);
      ctx.ellipse(p.pos[0], p.pos[1], size, size, 0, 0, 2 * Math.PI);
      ctx.fill();
    }
    for (const p of stars) {
      if (p.isDead) continue;
      if (p.age < 0) continue;
      const relAge = p.age / p.maxAge;
      ctx.globalAlpha = Math.max(0, 1 - relAge);
      ctx.translate(p.pos[0], p.pos[1]);
      ctx.rotate(p.speed + totalTime * 0.001 * p.speed);
      const size = Math.min(1, relAge * 10) * (1 / p.speed) * 1;
      ctx.scale(size, size);
      ctx.drawImage(ensureStarImg(p.color), -12, -12, 24, 24);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
  };

  let lastTime = 0;
  let totalTime = 0;
  let nodeScale = 1;
  const update = (dt: number): boolean => {
    if (dt === 0) return true;
    const targetNodeScale = totalTime < 150 ? 0.97 : totalTime < 400 ? 1.05 : 1.0;
    nodeScale +=
      (targetNodeScale - nodeScale) * (totalTime < 150 ? 0.05 : totalTime < 400 ? 0.15 : 0.25);
    node.style.transform = `scale(${nodeScale})`;

    let someAlive = false;
    const doIt = (p: Particle) => {
      if (p.isDead) return;
      p.age += dt;
      if (p.age < 0) {
        someAlive = true;
        return;
      }
      p.dir[0] *= 0.97;
      p.dir[1] *= 0.97;
      p.dir[1] -= -0.02;
      p.pos[0] += p.dir[0] * p.speed;
      p.pos[1] += p.dir[1] * p.speed;
      if (p.age > p.maxAge) {
        p.isDead = true;
      } else {
        someAlive = true;
      }
    };
    outerParticles.forEach(doIt);
    stars.forEach(doIt);
    return someAlive;
  };

  let id: number | null = null;
  const onTick = (dt: number) => {
    if (!lastTime) lastTime = dt;
    const diff = dt - lastTime;
    totalTime += diff;
    lastTime = dt;
    const someAlive = update(diff);
    if (!someAlive) {
      id = null;
      onDone();
    } else {
      draw();
      id = requestAnimationFrame(onTick);
    }
  };
  loadAllImgs().then(() => {
    draw();
    onTick(0);
  });
  return () => {
    if (id !== null) cancelAnimationFrame(id);
  };
};

const DEFAULT_SIZE = 200;

const encodeSvg = (svgString: string) =>
  svgString
    .replace(/"/g, "'")
    .replace(/%/g, "%25")
    .replace(/#/g, "%23")
    .replace(/{/g, "%7B")
    .replace(/}/g, "%7D")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")
    .replace(/\s+/g, " ");

const getStarImgSrc = (col: string) => {
  const rawSvg = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="24" height="24">
  <path fill="${col}" d="m22.04 10.74-3.36-1.1a6.94 6.94 0 0 1-4.4-4.39l-1.1-3.36a1.33 1.33 0 0 0-2.5 0l-1.1 3.36a6.93 6.93 0 0 1-4.32 4.4l-3.37 1.09a1.33 1.33 0 0 0 0 2.5l3.37 1.1a6.94 6.94 0 0 1 4.4 4.4l1.09 3.37a1.33 1.33 0 0 0 2.5 0l1.1-3.36a6.93 6.93 0 0 1 4.4-4.4l3.36-1.09a1.33 1.33 0 0 0 0-2.5l-.07-.02Z"/>
  </svg>`;

  return `data:image/svg+xml,${encodeSvg(rawSvg)}`;
};

let starImgEls = new Map<string, HTMLImageElement>();

const ensureStarImg = (
  col: string,
  cbs?: [res: () => void, rej: (arg: any) => void]
): HTMLImageElement => {
  const exist = starImgEls.get(col);
  if (exist) {
    if (cbs) cbs[0]();
    return exist;
  } else {
    const img = new Image();
    if (cbs) {
      img.onload = () => cbs[0]();
      img.onerror = (e) => cbs[1](e);
    }
    img.src = getStarImgSrc(col);
    starImgEls.set(col, img);
    return img;
  }
};

const DoneAnimCanvas = (props: {
  bounds: DOMRect;
  onDone: () => void;
  size?: number;
  node: HTMLElement;
}) => {
  const {bounds, size = DEFAULT_SIZE} = props;
  const propsRef = useRef(props);
  const canvasNodeRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    propsRef.current = props;
  });

  useLayoutEffect(() => {
    const canvasNode = canvasNodeRef.current!;
    return performAnim({
      ctx: canvasNode.getContext("2d")!,
      size: DEFAULT_SIZE,
      ...propsRef.current,
    });
  }, []);

  return (
    <canvas
      ref={canvasNodeRef}
      width={bounds.width + size * 2}
      height={bounds.height + size * 2}
      style={{
        position: "fixed",
        top: bounds.top - size,
        left: bounds.left - size,
        // background: "rgba(1,0,0,0.01)",
        pointerEvents: "none",
        zIndex: 1,
      }}
    />
  );
};

export const useDoneAnim = (nodeRef: MutableRefObject<HTMLElement | null>) => {
  const [node, setNode] = useState<null | JSX.Element>(null);
  const pendingResolvableRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => {
      if (pendingResolvableRef.current) {
        pendingResolvableRef.current();
      }
    };
  }, []);

  const handleStart = () =>
    new Promise<void>((res) => {
      const n = nodeRef.current;
      pendingResolvableRef.current = res;
      if (n) {
        const bounds = n.getBoundingClientRect();
        setNode(
          <DoneAnimCanvas
            bounds={bounds}
            onDone={() => {
              setNode(null);
              pendingResolvableRef.current = null;
              res();
            }}
            node={n}
          />
        );
      }
    });

  return {node, onStart: handleStart};
};
